import { z } from 'zod';

/*
 * O «Aprovador substituto» do próprio viajante (`/profile/substitute-approvers*` do
 * app-integration, #6909): os colegas que aprovam em nome dele enquanto ele está indisponível.
 * Vive no legado (business-service `userDelegates`), gravado pelo `businessUpdateUserProfile`. A
 * lista é trocada inteira, como no app legado.
 */

/** Dia de calendário `YYYY-MM-DD` (um dia real). */
const DaySchema = z.iso.date();

/** O período do substituto: um intervalo de dias (o primeiro e o último inclusos) ou
 *  «Tempo indeterminado». */
export const SubstituteApproverPeriodSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('undetermined') }),
  z.object({ kind: z.literal('range'), start: DaySchema, end: DaySchema }),
]);
export type SubstituteApproverPeriod = z.infer<typeof SubstituteApproverPeriodSchema>;

export const SubstituteApproverSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  email: z.string().nullable(),
  period: SubstituteApproverPeriodSchema,
});
export type SubstituteApprover = z.infer<typeof SubstituteApproverSchema>;

/** `GET /profile/substitute-approvers` (e a resposta do `PUT`). `enabled: false` = a empresa
 *  não deixa o viajante delegar (`hasDelegationAccess`): a tela esconde o grupo. */
export const SubstituteApproversViewSchema = z.object({
  enabled: z.boolean(),
  approvers: z.array(SubstituteApproverSchema),
});
export type SubstituteApproversView = z.infer<typeof SubstituteApproversViewSchema>;

/** Teto da lista: nenhum gestor precisa de tantos, e o corpo do `PUT` fica limitado. */
export const SUBSTITUTE_APPROVERS_MAX = 20;

/** `PUT /profile/substitute-approvers` — a lista inteira, sem colega repetido. */
export const SubstituteApproversWriteSchema = z
  .object({
    approvers: z
      .array(
        SubstituteApproverSchema.extend({
          name: z.string().trim().min(1).max(200),
          email: z.string().max(320).nullable(),
          period: SubstituteApproverPeriodSchema.refine(
            (period) => period.kind === 'undetermined' || period.start <= period.end,
            { message: 'end before start' },
          ),
        }).strict(),
      )
      .max(SUBSTITUTE_APPROVERS_MAX)
      .refine((rows) => new Set(rows.map((row) => row.id)).size === rows.length, {
        message: 'duplicate approver',
      }),
  })
  .strict();
export type SubstituteApproversWrite = z.infer<typeof SubstituteApproversWriteSchema>;

/** Um colega que pode ser substituto: `GET /profile/substitute-approvers/candidates?term=&page=`. */
export const SubstituteApproverCandidatesSchema = z.object({
  items: z.array(z.object({ id: z.string(), name: z.string(), email: z.string().optional() })),
  hasNext: z.boolean(),
});
export type SubstituteApproverCandidates = z.infer<typeof SubstituteApproverCandidatesSchema>;

/**
 * Recusas do fio. `delegation_not_allowed`: a empresa não deixa delegar. `start_in_past`: um
 * substituto NOVO começa antes de hoje (fuso de Brasília). `end_before_start` e `period_required`:
 * o período do legado. `invalid_request`: o corpo fora da forma.
 */
export const SUBSTITUTE_APPROVERS_ERROR_CODES = [
  'missing_user_identity',
  'not_found',
  'invalid_request',
  'delegation_not_allowed',
  'start_in_past',
  'end_before_start',
  'period_required',
  'unavailable',
] as const;
export type SubstituteApproversErrorCode = (typeof SUBSTITUTE_APPROVERS_ERROR_CODES)[number];
